/* eslint-disable eqeqeq */
/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useRef } from "react";
import { useSidebar } from "../../context/SidebarContext";
import {
  SignupWrapper,
  RegistrationContainer,
  StepperSection,
  LogoContainer,
  IllustrationContainer,
  StepperTextContent,
  StepperTitle,
  StepperDescription,
  ProgressIndicators,
  ProgressDot,
  FormSection,
  ProgressBarContainer,
  ProgressBar,
  StepperWrapper,
  HeaderText,
  NormalText,
  Typography,
  PrimaryText,
} from "../../styles/merchantRegistration";

// Step components
import PersonalInfoStep from "./steps/PersonalInfoStep";
import OTPVerificationStep from "./steps/OTPVerificationStep";
import StoreLocationStep from "./steps/StoreLocationStep";
import DocumentUploadStep from "./steps/DocumentUploadStep";

// Assets
import SignUpImage1 from "../../assets/svgs/LoginSignup/MerchantSignUp1.svg";
import SignUpImage2 from "../../assets/svgs/LoginSignup/MerchantSignUp2.svg";
import SignUpImage3 from "../../assets/svgs/LoginSignup/MerchantSignUp3.svg";
import Logo from "../../assets/svgs/LoginSignup/logo.svg";

import TermsAndConditionsStep from "./steps/TermsAndConditionsStep";
import BusinessHours from "./steps/BusinessHours";
import SignContract from "./steps/SignContract";

import { useNavigate, useParams } from "react-router-dom";
import {
  alertMessage,
  alertMessageWithCancel,
} from "../../services/alertService";
import { POST } from "../../services/HTTPServices";
import {
  LoadingOverlay,
  LoadingSpinner,
  LoadingSpinnerIcon,
} from "../../styles/loading";
import { ALERT_MESSAGES, ALERT_TITLES } from "../../constants";

const MerchantRegistration: React.FC = () => {
  const { isSidebarCollapsed = false } = useSidebar();
  const navigate = useNavigate();
  const isShowingConfirmRef = useRef(false);

  const { stepId } = useParams<{ stepId: string }>();

  // The URL is the source of truth for the current step.
  // Default to step 1 (index 0) if stepId is not present.
  const currentStep: any = stepId ? parseInt(stepId.split("-")[1], 10) - 1 : 0;

  const [currentLeftStep, setCurrentLeftStep] = useState(0);
  const [loading, setLoading] = React.useState(false);

  const [formData, setFormData] = useState({
    // Personal Info
    businessType: "personal",
    fullName: "",
    shopName: "",
    email: "",
    mobile: "",
    city: "Singapore",
    birthday: "",
    firstName: "",
    lastName: "",
    hawkerId: 0,
    password: "",
    confirmPassword: "",
    // OTP
    otp: "",
    // Business Type
    businessCategory: "",
    // Store Location
    postcode: "",
    streetName: "",
    buildingName: "",
    block: "",
    floor: "",
    unit: "",
    // Documents
    sfaLicense: null,
    halalCertificate: null,
    nricFront: null,
    letterOfAuth: null,
    // Menu
    menuCatalog: null,
    storeCover1: null,
    storeCover2: null,
    serviceProviderServices: [
      {
        serviceTypeId: 5,
      },
    ],
    openingHours: [],
  });

  useEffect(() => {
    const currentPath = window.location.pathname.split("/")[1];
    if (currentPath === "merchant-registration-kyb") {
      navigate(`/merchant-registration/step-6`);
    }
  }, [window.location.pathname]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setCurrentLeftStep((prevIndex) => (prevIndex === 2 ? 0 : prevIndex + 1));
    }, 3000);

    return () => clearTimeout(timer); // Cleanup function
  }, [currentLeftStep]);

  const [shouldBlockNavigation, setShouldBlockNavigation] = useState(
    currentStep == 2 || currentStep == 3 || currentStep == 5
  );

  useEffect(() => {
    // Update shouldBlockNavigation when currentStep changes
    setShouldBlockNavigation(
      currentStep == 2 || currentStep == 3 || currentStep == 5
    );
  }, [currentStep]);

  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (
        shouldBlockNavigation ||
        currentStep == 2 ||
        currentStep == 3 ||
        currentStep == 5
      ) {
        e.preventDefault();
        return ALERT_MESSAGES.UNLOAD_WARNING;
      }
    };

    const handlePopState = async (e: PopStateEvent) => {
      // Use a small delay to ensure React has finished rendering
      setTimeout(async () => {
        const isBlockingStep =
          currentStep == 2 || currentStep == 3 || currentStep == 5;

        if (isBlockingStep && !isShowingConfirmRef.current) {
          // Prevent the navigation by pushing the current state back
          window.history.pushState(null, "", window.location.pathname);

          isShowingConfirmRef.current = true;

          try {
            const confirmLeave = await alertMessageWithCancel(
              ALERT_TITLES.WARNING,
              ALERT_MESSAGES.UNLOAD_WARNING
            );

            if (confirmLeave) {
              // Remove event listeners to prevent blocking
              window.removeEventListener("beforeunload", handleBeforeUnload);
              window.removeEventListener("popstate", handlePopState);
              rollback(formData.email, formData.mobile);
            } else {
            }
          } catch (error) {
            console.error("Error in confirmation dialog:", error);
          } finally {
            isShowingConfirmRef.current = false;
          }
        } else {
        }
      }, 10); // Small delay to ensure React state is updated
    };

    // Add event listeners
    window.addEventListener("beforeunload", handleBeforeUnload);
    window.addEventListener("popstate", handlePopState);

    // Push initial state to ensure popstate fires consistently
    // Only push if we're on a blocking step
    if (currentStep == 2 || currentStep == 3 || currentStep == 5) {
      // Use setTimeout to ensure this runs after React has finished rendering
      setTimeout(() => {
        window.history.pushState(
          { step: currentStep, timestamp: Date.now() },
          "",
          window.location.pathname
        );
      }, 0);
    }

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      window.removeEventListener("popstate", handlePopState);
    };
  }, [currentStep, formData.email, formData.mobile]); // Remove shouldBlockNavigation from deps

  // Alternative approach: Use a separate effect for immediate blocking
  useEffect(() => {
    if (currentStep == 2 || currentStep == 3 || currentStep == 5) {
      // Immediately set up history state for blocking steps
      const setupHistoryState = () => {
        window.history.pushState(
          { step: currentStep, blockNavigation: true },
          "",
          window.location.pathname
        );
      };

      // Use requestAnimationFrame to ensure DOM is ready
      requestAnimationFrame(setupHistoryState);
    }
  }, [currentStep]);

  const rollback = async (email: any, mobile: any) => {
    setLoading(true);
    let url = "serviceprovider/RollbackMerchant";
    let temp = {
      email: email,
      mobile: "+65" + mobile,
    };
    let res: any = await POST(url, temp, "provider");
    if (res && res.status === 200) {
      Object.keys(localStorage)
        .filter((k) => k !== "login_credentials")
        .forEach((k) => localStorage.removeItem(k));
      setLoading(false);
      navigate("/login");
    } else {
      setLoading(false);
      if (res.status && res.status === 401) navigate("/unauthorized-page");
      else {
        alertMessage(ALERT_TITLES.ERROR, res.message);
      }
    }
  };

  const steps = [
    {
      title: "Easy Order",
      SubTitle: "Management",
      description:
        "Manage your order and delivery smoothly with our simple app.",
      image: SignUpImage1,
    },
    {
      title: "Promote Your",
      SubTitle: "Business",
      description:
        "Let everyone know about your restaurant and special deals through our app.",
      image: SignUpImage2,
    },
    {
      title: "Reach More",
      SubTitle: "Customers",
      description:
        "Get your food business seen by more people through our app.",
      image: SignUpImage3,
    },
  ];

  const components = [
    { component: PersonalInfoStep },
    { component: TermsAndConditionsStep },
    { component: OTPVerificationStep },
    { component: StoreLocationStep },
    { component: BusinessHours },
    { component: DocumentUploadStep },
    { component: SignContract },
  ];

  const handleChange = (field: string, value: any) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleAssign = (value: any) => {
    setFormData(value);
  };

  const handleNext = () => {
    const nextStep = currentStep + 1;

    if (nextStep < components.length) {
      navigate(`/merchant-registration/step-${nextStep + 1}`);
    } else {
    }
  };

  const handleBack = async () => {
    if (currentStep == 2 || currentStep == 3 || currentStep == 5) {
      const confirmLeave = await alertMessageWithCancel(
        ALERT_TITLES.WARNING,
        ALERT_MESSAGES.UNLOAD_WARNING
      );

      if (confirmLeave) {
        rollback(formData.email, formData.mobile);
      } else {
      }
    } else if (currentStep > 0) {
      const prevStep = currentStep - 1;
      navigate(`/merchant-registration/step-${prevStep + 1}`);
    } else {
      navigate(`/login`);
      //rollback(formData.email, formData.mobile);
    }
  };
  // Calculate progress percentage
  const progressPercentage = ((currentStep + 1) / components.length) * 100;

  // Get current step component, handle out-of-bounds index
  const StepComponent = components[currentStep]
    ? components[currentStep].component
    : null;

  const currentStepData = steps[currentLeftStep];

  const displayText = () => {
    switch (currentLeftStep) {
      case 0: {
        return (
          <>
            <HeaderText>
              <NormalText>Reach More </NormalText>
              <PrimaryText>Customers</PrimaryText>
            </HeaderText>
            <Typography>
              Get your food business seen by more people through our app.
            </Typography>
          </>
        );
      }
      case 1: {
        return (
          <>
            <HeaderText>
              <NormalText>Easy </NormalText>
              <PrimaryText>Order </PrimaryText>
              <NormalText>Management</NormalText>
            </HeaderText>
            <Typography>
              Manage your order and delivery smoothly with our simple app.
            </Typography>
          </>
        );
      }
      case 2: {
        return (
          <>
            <HeaderText>
              <NormalText>Promote Your </NormalText>
              <PrimaryText>Business</PrimaryText>
            </HeaderText>
            <Typography>
              Let everyone know about your restaurant and special deals through
              our app.{" "}
            </Typography>
          </>
        );
      }
    }
  };

  let path = window.location.pathname.split("/");
  let changedRole = path[1] === "change-role" ? true : false;

  return (
    <>
      {loading && (
        <LoadingOverlay>
          <LoadingSpinner>
            <LoadingSpinnerIcon />
          </LoadingSpinner>
        </LoadingOverlay>
      )}

      <SignupWrapper>
        <RegistrationContainer $isSidebarCollapsed={isSidebarCollapsed}>
          {/* Left side - Stepper with illustrations */}
          <StepperWrapper>
            <StepperSection>
              <LogoContainer>
                <img src={Logo} alt="Logo" />
              </LogoContainer>

              <IllustrationContainer>
                <img
                  src={currentStepData.image}
                  alt="Registration illustration"
                />
              </IllustrationContainer>

              <StepperTextContent>
                <StepperTitle>{displayText()}</StepperTitle>
                <StepperDescription>
                  {currentStepData.description}
                </StepperDescription>
              </StepperTextContent>

              <ProgressIndicators>
                {steps.map((_, index) => (
                  <ProgressDot
                    key={index}
                    $active={index === currentLeftStep}
                  />
                ))}
              </ProgressIndicators>
            </StepperSection>
          </StepperWrapper>

          {/* Right side - Form content */}
          <FormSection>
            <ProgressBarContainer>
              <ProgressBar $progress={progressPercentage} />
            </ProgressBarContainer>

            {StepComponent && (
              <StepComponent
                formData={formData}
                onChange={handleChange}
                onAssign={handleAssign}
                onNext={handleNext}
                onBack={handleBack}
                changedRole={changedRole}
              />
            )}
          </FormSection>
        </RegistrationContainer>
      </SignupWrapper>
    </>
  );
};

export default MerchantRegistration;
